import type { Brand } from '@seek/melways-sites';

import type { Customisations } from '../modules/customisation';
import type {
  ClientConfig,
  ClientLocaleConfig,
} from '../modules/customisation/types';

import arrayIncludes from './arrayIncludes';
import getSearchParams from './getSearchParams';
import { getLanguageLocaleForMobile } from './mobile/forMobile';

interface ClientMetadata {
  locale: string;
  language: string;
  brand?: Brand;
  isMobileClient?: boolean;
}

const DEFAULT_LOCALE = 'au';
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_EXPERIENCE = 'candidate';
export const ALLOWED_LOCALES = ['au', 'nz', 'my', 'ph', 'id', 'sg', 'th', 'hk'];

const getClientConfig = (
  site: string,
  clientId: string = '',
  clientDefaultConfigs: ClientLocaleConfig = {}, // have set default {} here for the site where no 'clientConfig' is present
): ClientConfig => {
  // This is the client default values for localization
  let clientConfig = {
    ...(site === 'seekAnz' && {
      isMobileClient: false,
    }),
  };

  // Set client default values from customisations.clientConfig depending on clientId
  Object.keys(clientDefaultConfigs).every((key) => {
    if (key.split(',').includes(clientId)) {
      clientConfig = clientDefaultConfigs[key];
      return false;
    }
    return true;
  });

  return clientConfig;
};

export default (
  site: string, // site is required,
  route: string, // route is required,
  clientId?: string, // edge case is - if no clientId passed
  customisations?: Customisations, // edge case is - if no customisations object
): ClientMetadata => {
  const {
    clientConfig,
    localeConfig,
    experience = DEFAULT_EXPERIENCE,
  } = customisations ?? {};

  const { isMobileClient, allowedLanguages: clientAllowedLanguages } =
    getClientConfig(site, clientId, clientConfig);

  const { locale: inputLocale, language: inputLanguage } =
    isMobileClient && route !== '/lo/reset'
      ? getLanguageLocaleForMobile()
      : getSearchParams();

  const locale =
    !inputLocale || !arrayIncludes(inputLocale, ALLOWED_LOCALES)
      ? DEFAULT_LOCALE
      : inputLocale;

  const {
    defaultLanguage = DEFAULT_LANGUAGE,
    allowedLanguages: localeAllowedLanguages = [DEFAULT_LANGUAGE],
  } = localeConfig?.[locale] ?? {};
  const brand = localeConfig?.[locale]?.[experience]?.brand;
  const allowedLanguages = clientAllowedLanguages || localeAllowedLanguages;

  let language =
    !inputLanguage || !arrayIncludes(inputLanguage, allowedLanguages)
      ? defaultLanguage
      : inputLanguage;

  language = `${language}-${locale.toUpperCase()}`;

  return { language, locale, brand, isMobileClient };
};
